import { BaseService } from "./baseService";
import {PublicUserInfo} from "@/app/service/types";

export interface Stage {
  stageArn: string;
  groupId: string;
  eventKey: string;
  status: string;
  channelArn: string;
  channelUrl: string;
}

export interface Event {
  eventKey: string;
  description: string;
  imageUrl: string;
  status: string;
  scheduledStartDatetime: string;
  scheduledEndDatetime: string | null;
  eventDetails: any | null;
}

export interface Recording {
  compositionArn: string;
  recordingPrefix: string;
  state: string;
  startTime: string;
  recordingFormat: string;
  isVisible: boolean;
  isFeatured: boolean;
  stage: Stage;
  event: Event;
  manifestKeys: string[];
  key?: string;
  lastModified?: Date;
  size?: number;
  user: PublicUserInfo;
}

export interface RecordingsResponse {
  recordings: Recording[];
  total: number;
  format: string;
  source: string;
  note: string;
  // Legacy debug field for backward compatibility
  debug?: {
    totalFiles: number;
    bucketName: string;
    sampleFiles: string[];
    allM3u8Files: string[];
    hlsRecordingsCount: number;
  };
}

export interface PlaybackUrlResponse {
  playbackUrl: string;
  recordingKey: string;
  expiresIn: number;
  format: string;
  contentType: string;
}

export class PlaybackService extends BaseService {
    /**
     * Get list of available recordings
     */
    static async getRecordings(): Promise<RecordingsResponse> {
        const response = await this.get("/playback/recordings");

        if (!response.ok) {
            throw new Error(`Failed to fetch recordings: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Get playback URL for a specific recording
     */
    static async getPlaybackUrl(recordingKey: string, expiresIn: number = 3600): Promise<PlaybackUrlResponse> {
        const encodedKey = encodeURIComponent(recordingKey);
        const response = await this.get(`/playback/${encodedKey}?expires=${expiresIn}`);

        if (!response.ok) {
            throw new Error(`Failed to get playback URL: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Get the best manifest key for playback (prefer multivariant, then master, then playlist)
     */
    static getBestManifestKey(recording: Recording): string | null {
        if (!recording.manifestKeys || recording.manifestKeys.length === 0) {
            return recording.key || null;
        }

        // Prefer multivariant, then master, then playlist
        const preferredOrder = ['multivariant.m3u8', 'master.m3u8', 'playlist.m3u8'];

        for (const preferred of preferredOrder) {
            const match = recording.manifestKeys.find(key => key.endsWith(preferred));
            if (match) {
                return match;
            }
        }

        // Fallback to first available manifest key
        return recording.manifestKeys[0] || recording.key || null;
    }

    /**
     * Set visibility for multiple recordings
     */
    static async setVisibility(compositionArns: string[], isVisible: boolean): Promise<void> {
        const response = await this.post("/playback/set-visibility", {
            compositionArns,
            isVisible
        });

        if (!response.ok) {
            throw new Error(`Failed to set visibility: ${response.status}`);
        }
    }

    /**
     * Set featured status for multiple recordings
     */
    static async setFeatured(compositionArns: string[], isFeatured: boolean): Promise<void> {
        const response = await this.post("/playback/set-featured", {
            compositionArns,
            isFeatured
        });

        if (!response.ok) {
            throw new Error(`Failed to set featured status: ${response.status}`);
        }
    }
}
